import { useState, useEffect } from 'react';
import { reportsApi, ReportResponse, BranchReportData } from '../../api/reports.api';
import { categoriesApi, Category } from '../../api/categories.api';
import { branchesApi, Branch } from '../../api/branches.api';
import { brandsApi, Brand } from '../../api/brands.api';
import Button from '../../components/forms/Button';
import Select from '../../components/forms/Select';
import Loading from '../../components/common/Loading';
import BranchReport from './components/BranchReport';
import { generateBranchReportPDF } from '../../utils/pdfGenerator';

const BranchReportPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<ReportResponse<BranchReportData> | null>(null);

  const [categories, setCategories] = useState<Category[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);

  const [branchId, setBranchId] = useState<string>('all');
  const [categoryId, setCategoryId] = useState<string>('all');
  const [brandId, setBrandId] = useState<string>('all');
  const [status, setStatus] = useState<string>('all');

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const [categoriesData, branchesData, brandsData] = await Promise.all([
        categoriesApi.getAll(),
        branchesApi.getAll(),
        brandsApi.getAll(),
      ]);
      setCategories(categoriesData.filter(c => c.isActive).sort((a, b) => a.name.localeCompare(b.name, 'tr')));
      setBranches(branchesData.filter(b => b.isActive).sort((a, b) => a.name.localeCompare(b.name, 'tr')));
      setBrands(brandsData.filter(b => b.isActive).sort((a, b) => a.name.localeCompare(b.name, 'tr')));
    } catch (err: any) {
      console.error('Filtre seçenekleri yüklenirken hata:', err);
    }
  };

  const handleGenerateReport = async () => {
    setIsLoading(true);
    setError(null);
    setReportData(null);

    try {
      const filters: any = {};
      if (branchId && branchId !== 'all') filters.branchId = branchId;
      if (categoryId && categoryId !== 'all') filters.categoryId = categoryId;
      if (brandId && brandId !== 'all') filters.brandId = brandId;
      if (status && status !== 'all') filters.status = status;

      const data = await reportsApi.getBranchReport(filters);
      setReportData(data);
    } catch (err: any) {
      setError(err.message || 'Rapor oluşturulurken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearFilters = () => {
    setBranchId('all');
    setCategoryId('all');
    setBrandId('all');
    setStatus('all');
    setReportData(null);
  };

  const handleExportPDF = () => {
    if (reportData) {
      generateBranchReportPDF(reportData);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Şube Bazlı Cihaz Raporu</h1>
          <p className="text-sm text-gray-600 mt-1">Şubelere göre cihaz dağılımını görüntüleyin</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Filtreler</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Select
            label="Şube"
            value={branchId}
            onChange={(value) => setBranchId(value)}
            options={[
              { value: 'all', label: 'Tüm Şubeler' },
              ...branches.map(b => ({ value: b.id, label: b.name }))
            ]}
            searchable={true}
          />

          <Select
            label="Kategori"
            value={categoryId}
            onChange={(value) => setCategoryId(value)}
            options={[
              { value: 'all', label: 'Tüm Kategoriler' },
              ...categories.map(c => ({ value: c.id, label: c.name }))
            ]}
            searchable={true}
          />

          <Select
            label="Marka"
            value={brandId}
            onChange={(value) => setBrandId(value)}
            options={[
              { value: 'all', label: 'Tüm Markalar' },
              ...brands.map(b => ({ value: b.id, label: b.name }))
            ]}
            searchable={true}
          />

          <Select
            label="Durum"
            value={status}
            onChange={(value) => setStatus(value)}
            options={[
              { value: 'all', label: 'Tüm Durumlar' },
              { value: 'Available', label: 'Şubede' },
              { value: 'Assigned', label: 'Zimmetli' },
              { value: 'InRepair', label: 'Onarımda' },
              { value: 'Retired', label: 'Kullanım Dışı' },
              { value: 'Scrapped', label: 'Hurda' },
            ]}
            searchable={true}
          />
        </div>

        <div className="flex gap-3 mt-6">
          <Button onClick={handleGenerateReport} disabled={isLoading}>
            {isLoading ? 'Rapor Oluşturuluyor...' : 'Rapor Oluştur'}
          </Button>
          <Button variant="outline" onClick={handleClearFilters}>
            Filtreleri Temizle
          </Button>
          {reportData && (
            <Button variant="secondary" onClick={handleExportPDF}>
              PDF Olarak Kaydet
            </Button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Loading */}
      {isLoading && <Loading text="Rapor oluşturuluyor..." />}

      {/* Report Display */}
      {reportData && !isLoading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <BranchReport data={reportData} />
        </div>
      )}
    </div>
  );
};

export default BranchReportPage;
